import { ratingMethodToIntegrationType } from '@shared/components/money-form/components/market-conditions/MarketConditionsContainer';
import { RatingMethod } from '@shared/generated/graphql';
import {
  CarrierRate,
  DATRatingMethod,
  TriumphRatingMethod,
} from '@shared/types/quote';
import { selectDefaultDrayratesEntry } from '@shared/utils/rates/drayrates';
import {
  getRateMapKey,
  isManualAdjustment,
  rateToLabel,
} from '@shared/utils/rates/rates';
import { filterRateComponents } from 'clerk_common/types';
import { useMemo } from 'react';
import { getGroupedRatesUrl } from '../../utils/getGroupedRatesUrl';
import { GroupedRates } from './grouped-rates/GroupedRates';
import { GroupedRate } from './grouped-rates/types';
import { RatingWidgetProps } from './types';

const isExcludedIntegration = (rc: CarrierRate) => {
  return rc.ratingMethod === RatingMethod.ARCBEST_TL_QUOTING;
};

export const selectDefaultRate = ({
  rates,
  ratingMethod,
  defaultDrayratesRatingMethod,
}: {
  rates: GroupedRate[];
  ratingMethod: RatingMethod;
  defaultDrayratesRatingMethod?: string | null;
}): GroupedRate | undefined => {
  switch (ratingMethod) {
    case RatingMethod.DAT:
      return (
        rates.find((r) => r.metadata?.ratingMethod === DATRatingMethod.RATE) ||
        rates[0]
      );

    case RatingMethod.GREENSCREENS:
      return (
        rates.find(
          (r) => r.metadata?.ratingMethod === TriumphRatingMethod.DEFAULT
        ) || rates[0]
      );
    case RatingMethod.DRAYRATES_RATING:
      return selectDefaultDrayratesEntry(rates, defaultDrayratesRatingMethod);

    default:
      return rates[0];
  }
};

function toGroupedRate(r: CarrierRate): GroupedRate {
  return {
    id: r.id,
    label: rateToLabel(r),
    key: getRateMapKey(r),
    ratingMethod: r.ratingMethod,
    metadata: r.metadata,
    rate:
      filterRateComponents(r.computedRate?.rateComponents)
        .filter((rc) => !isManualAdjustment(rc))
        .reduce((acc, curr) => acc + curr.value, 0) ?? 0,
  };
}

export function LegacyRatingWidget(p: RatingWidgetProps) {
  const rates: GroupedRate[] = p.carrierRates
    .filter((r) => !isExcludedIntegration(r))
    .map((r) => toGroupedRate(r));
  const integrationType = ratingMethodToIntegrationType(p.ratingMethod);
  const integrationConfig = integrationType
    ? p.quote.organization.integrationConfigs?.integrations.find(
        (config) => config.integrationType === integrationType
      )
    : undefined;

  const defaultRate = selectDefaultRate({
    rates,
    ratingMethod: p.ratingMethod,
    defaultDrayratesRatingMethod: integrationConfig?.defaultRatingMethod,
  });

  const isTabi = p.ratingMethod === RatingMethod.TABI;

  const groupUrl = useMemo(
    () =>
      getGroupedRatesUrl({
        ratingMethod: p.ratingMethod,
        rates,
        quote: p.quote,
      }),
    [p]
  );

  return (
    <GroupedRates
      rates={rates}
      ratingMethod={p.ratingMethod}
      quote={p.quote}
      onExpandAnyRateWidget={p.onExpandAnyRateWidget}
      preventRateComparisons={p.preventRateComparisons}
      focusedRatingWidget={p.focusedRatingWidget}
      rateSourceUrl={groupUrl}
      defaultExpanded={isTabi}
      defaultRate={defaultRate}
    />
  );
}
